import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { partition } from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { IBaseEntityType, IToolbarButtonDefinition } from '@shared/types';

const HEADER_BUTTON_VALUES = ['ok', 'cancel', 'done', 'save', 'resetColumn'];

@Component({
    selector: 'dm-modal-wrapper',
    templateUrl: 'modal-wrapper.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalWrapperComponent implements OnInit, OnDestroy {
    @Input() public title: string;
    @Input() public customData: {
        cssClass?: string;
        keyboard?: boolean;
        backdrop?: boolean | 'static';
    } = {};

    @Input()
    set isShowModal(value: boolean) {
        this._isShowModal = !!value;
        if (this._isShowModal) {
            this.openModal();
        } else {
            this.closeModal();
        }
    }
    get isShowModal(): boolean {
        return this._isShowModal;
    }

    @Input()
    set buttons(value: IToolbarButtonDefinition[]) {
        this._buttons = value || [];
        this.initButtons();
    }
    get buttons(): IToolbarButtonDefinition[] {
        return this._buttons;
    }

    @Output() submitModal = new EventEmitter<IBaseEntityType>();
    @Output() cancelModal = new EventEmitter<IBaseEntityType>();

    @ViewChild('dmModalWrapper', { static: true }) private modalTemplate: TemplateRef<void>;

    public headerButtons: IToolbarButtonDefinition[] = [];
    public footerButtons: IToolbarButtonDefinition[] = [];

    private modalRef: BsModalRef;
    private onHiddenSub?: Subscription;

    private _isShowModal = false;
    private _buttons: IToolbarButtonDefinition[] = [
        { value: 'ok', caption: 'Ok', class: 'btn-primary pull-right', click: () => this.submitModal.emit() },
        { value: 'cancel', caption: 'Cancel', class: 'btn-default pull-right', click: () => this.cancelModal.emit() },
    ];

    constructor(private modalService: BsModalService) {}

    public ngOnInit() {
        this.initButtons();
    }

    public ngOnDestroy() {
        this.onHiddenSub?.unsubscribe();
        this.closeModal();
    }

    public initButtons() {
        [this.headerButtons, this.footerButtons] = partition(this._buttons, (button: IToolbarButtonDefinition) =>
            HEADER_BUTTON_VALUES.includes(button.value),
        );
    }

    private openModal(): void {
        if (this.modalRef) {
            return;
        }

        const config = {
            class: this.customData?.cssClass,
            keyboard: this.customData?.keyboard || false,
            backdrop: this.customData?.backdrop || 'static',
            ignoreBackdropClick: this.customData?.backdrop === 'static',
        };

        this.modalRef = this.modalService.show(this.modalTemplate, config);

        const currentModalRef = this.modalRef;
        this.onHiddenSub?.unsubscribe();
        this.onHiddenSub = this.modalService.onHidden.pipe(take(1)).subscribe(() => {
            if (this.modalRef === currentModalRef && this.isShowModal) {
                this.cancelModal.emit();
            }
            this.modalRef = null;
        });
    }

    private closeModal(): void {
        if (this.modalRef) {
            this.modalRef.hide();
        }
    }
}
